import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { siteData } from '@/mocks/siteData';
import Breadcrumb from '@/components/feature/Breadcrumb';
import type { BreadcrumbItem } from '@/components/feature/Breadcrumb';

export default function About() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [formUrl, setFormUrl] = useState('');

  useEffect(() => {
    import('@/utils/getFormUrl').then((mod) => {
      mod.default('黄油游戏官方指南联系表单').then((url: string) => {
        setFormUrl(url);
      });
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setSubmitted(true);
      return;
    }

    try {
      if (!formUrl) {
        setSubmitted(true);
        return;
      }

      const body = new URLSearchParams();
      (formData as unknown as [string, string][]).forEach(([key, value]) => {
        if (key !== 'phone_alt') {
          body.append(key, value);
        }
      });

      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (res.ok && parsed.code === 'OK') {
        setSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setError(serverMsg || t('form.subscribeError'));
      }
    } catch {
      setError(t('form.subscribeError'));
    }
  };

  const breadcrumbItems: BreadcrumbItem[] = [{ label: t('breadcrumb.about') }];

  return (
    <div className="min-h-screen pt-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "url": "https://example.com/about",
            "name": "关于我们 - 黄油游戏官方指南",
            "description": "了解黄油游戏官方指南的品牌故事、发展历程与团队信息",
            "isPartOf": {
              "@type": "WebSite",
              "name": "黄油游戏官方指南",
              "url": "https://example.com/"
            },
            "about": {
              "@type": "Organization",
              "name": "黄油游戏官方指南",
              "description": siteData.description,
              "url": "https://example.com/",
              "email": siteData.email,
              "address": siteData.address,
              "telephone": siteData.phone,
            },
          }),
        }}
      />

      <Breadcrumb items={breadcrumbItems} />

      <section className="relative w-full h-[280px] md:h-[380px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20warm%20and%20professional%20team%20workspace%20with%20soft%20golden%20lighting%2C%20collaborative%20atmosphere%2C%20modern%20creative%20office%2C%20warm%20amber%20and%20cream%20tones%2C%20abstract%20artistic%20composition%2C%20minimalist%20and%20elegant%20interior%2C%20soft%20bokeh%20background&width=1600&height=600&seq=about-hero-2026&orientation=landscape"
          alt="关于黄油游戏官方指南"
          className="absolute inset-0 w-full h-full object-cover"
          width="1600"
          height="600"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-2xl md:text-4xl font-heading text-background-50 mb-3">关于我们</h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl mx-auto">了解黄油游戏官方指南的品牌故事与使命</p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">{siteData.brandStory.title}</h2>
          <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4 whitespace-pre-line">
            {siteData.brandStory.content}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">发展里程碑</h2>
          <div className="relative">
            <div className="absolute left-3.5 md:left-4 top-0 bottom-0 w-px bg-primary-200"></div>
            <div className="space-y-6 md:space-y-8">
              {siteData.brandStory.milestones.map((milestone, idx) => (
                <div key={idx} className="flex items-start gap-4 md:gap-6">
                  <div className="relative z-10 w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 flex-shrink-0 mt-0.5">
                    <div className={`${idx === siteData.brandStory.milestones.length - 1 ? 'w-2 h-2' : 'w-1.5 h-1.5'} rounded-full bg-current`}></div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-primary-500 whitespace-nowrap">{milestone.year}</span>
                    <p className="text-sm md:text-base text-foreground-700 mt-0.5">{milestone.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">{siteData.reputation.title}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
            {siteData.reputation.testimonials.map((item, idx) => (
              <div key={idx} className="bg-background-50 rounded-lg p-4 md:p-5 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: item.rating }).map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center text-accent-500">
                      <i className="ri-star-fill text-sm"></i>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-3">&ldquo;{item.content}&rdquo;</p>
                <div>
                  <p className="text-sm font-medium text-foreground-950">{item.name}</p>
                  <p className="text-xs text-foreground-400">{item.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-2 text-center">联系我们</h2>
          <p className="text-sm text-foreground-500 text-center mb-6 md:mb-8">有任何关于黄油游戏的问题或建议？欢迎联系我们</p>

          <div className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100 text-accent-500">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <p className="text-base font-medium text-foreground-950 mb-1">消息已发送！</p>
                <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快回复。</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form="">
                {error && (
                  <div className="mb-4 px-4 py-3 rounded-md bg-accent-100/70 text-accent-700 text-sm">{error}</div>
                )}
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-3.5 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                  </div>
                  <div className="anti-spam-wrapper">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {t('button.submit')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}